import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { ArrowLeft } from "lucide-react";
import { LEGAL, LEGAL_LINKS } from "@/lib/legal";

// Route-driven policy renderer. /privacy, /terms, /user-agreement all use this page.
export default function Policy({ docKey }) {
  const params = useParams();
  const key = docKey || params.doc || "privacy";
  const map = { privacy: "privacy", terms: "terms", "user-agreement": "agreement" };
  const doc = LEGAL[map[key]];
  if (!doc) return <Navigate to="/privacy" replace />;

  return (
    <div className="min-h-screen bg-sand" data-testid={`policy-${key}`}>
      <header className="border-b hairline bg-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-[13px] text-ink/65 inline-flex items-center hover:text-ink">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Link>
        </div>
      </header>

      <section className="bg-court text-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-2"><div className="eyebrow-light">Legal</div></div>
          <div className="lg:col-span-7">
            <h1 className="font-display text-[38px] lg:text-[56px] balanced" style={{ fontWeight: 400, lineHeight: 1.02 }} data-testid="policy-title">
              {doc.title}
            </h1>
          </div>
          <div className="lg:col-span-3 text-sand/70 font-mono text-[11px] tracking-[0.12em] uppercase">
            Effective · {doc.effective}
          </div>
        </div>
      </section>

      <main className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <div className="eyebrow mb-4">Documents</div>
          <ul className="border-t hairline">
            {LEGAL_LINKS.map((l) => (
              <li key={l.slug} className="border-b border-stone/40">
                <Link
                  to={`/${l.slug}`}
                  data-testid={`legal-link-${l.slug}`}
                  className={`block py-3.5 font-display text-[16px] transition-colors ${key === l.slug ? "text-clay" : "text-ink/80 hover:text-ink"}`}
                  style={{ fontWeight: 400 }}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-8 font-mono text-[11px] tracking-[0.12em] uppercase text-stone leading-relaxed">
            Questions?<br />
            <a href="mailto:legal@e-sulah.com" className="text-clay">legal@e-sulah.com</a>
          </div>
        </aside>

        <article className="lg:col-span-9 max-w-3xl">
          {doc.intro && <p className="text-ink/80 text-[15px] leading-relaxed mb-10 border-l-2 border-clay pl-5">{doc.intro}</p>}
          <div className="space-y-12">
            {doc.sections.map((s, i) => (
              <section key={i} data-testid={`section-${i}`}>
                <h2 className="font-display text-[22px] lg:text-[26px] text-court" style={{ fontWeight: 400, lineHeight: 1.15 }}>{s.h}</h2>
                {s.p && <p className="mt-4 text-ink/80 text-[15px] leading-relaxed">{s.p}</p>}
                {s.bullets && (
                  <ul className="mt-4 space-y-2 text-ink/80 text-[15px] leading-relaxed">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="grid grid-cols-[16px_1fr] gap-3">
                        <span className="text-clay mt-0.5">·</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.groups && (
                  <div className="mt-4 space-y-4 border-l-2 border-clay pl-5">
                    {s.groups.map((g, j) => (
                      <div key={j}>
                        <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-clay">{g.k}</div>
                        <p className="text-ink/80 text-[14px] mt-1 leading-relaxed">{g.b}</p>
                      </div>
                    ))}
                  </div>
                )}
                {s.after && <p className="mt-4 text-ink/75 text-[14px] leading-relaxed italic">{s.after}</p>}
                {s.subs && (
                  <div className="mt-6 space-y-8">
                    {s.subs.map((sub, k) => (
                      <div key={k}>
                        <h3 className="font-display text-[18px] text-ink" style={{ fontWeight: 500 }}>{sub.h}</h3>
                        {sub.p && <p className="mt-2 text-ink/80 text-[15px] leading-relaxed">{sub.p}</p>}
                        {sub.bullets && (
                          <ul className="mt-3 space-y-2 text-ink/80 text-[15px]">
                            {sub.bullets.map((b, m) => (
                              <li key={m} className="grid grid-cols-[16px_1fr] gap-3">
                                <span className="text-clay mt-0.5">·</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-16 border-t hairline pt-6 font-mono text-[11px] tracking-[0.12em] uppercase text-stone">
            End of document · Last updated {doc.effective}
          </div>
        </article>
      </main>
    </div>
  );
}
